import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Moment} from 'moment';
import * as moment from 'moment';

import {ObservationType, PK} from '../models/models';
import {EnumService} from './enum.service';
import {GeometricDataService} from './geometric-data.service';
import {MeasureService} from './measure.service';
import {ObservationTypesService} from './observation-types.service';
import {PKLocalService} from './local-data-services/pk-local.service';
import {PKService} from './pk.service';
import {UpdateLocalService} from './local-data-services/update-local.service';

@Injectable()
export class UpdateService {

  private pages: number = 0;
  private storedPages: number = 0;
  private pkTotalPagesAdded: boolean = false;
  private updateCurrentlyRunning : boolean = false;

  constructor(private enumService: EnumService,
              private geometricDataService: GeometricDataService,
              private measureService: MeasureService,
              private observationTypesService: ObservationTypesService,
              private pkLocalService: PKLocalService,
              private pkService: PKService) {
  }

  checkUpdateNeed(): Observable<boolean> {
    if (!this.updateCurrentlyRunning) {
      return this.getLastUpdates().map(
        (dateStrings: string[]) => {
          let updateNeeded: boolean = false;
          let dates = dateStrings.map((date: string) => this.dateStringToMoment(date));
          let storedMeasuresLastUpdateDate = this.dateStringToMoment(UpdateLocalService.getMeasuresLastUpdate());
          let storedObservationTypesLastUpdateDate = this.dateStringToMoment(UpdateLocalService.getObservationTypesLastUpdate());
          let storedPksLastUpdateDate = this.dateStringToMoment(UpdateLocalService.getPksLastUpdate());


          if (!storedMeasuresLastUpdateDate.isValid() || storedMeasuresLastUpdateDate.isBefore(dates[0])) {
            updateNeeded = true;
            UpdateLocalService.setMeasuresLastUpdate(this.momentToDateString(dates[0]));
          }
          if (!storedObservationTypesLastUpdateDate.isValid() || storedObservationTypesLastUpdateDate.isBefore(dates[1])) {
            updateNeeded = true;
            UpdateLocalService.setObservationTypesLastUpdate(this.momentToDateString(dates[1]));
          }
          if (!storedPksLastUpdateDate.isValid() || storedPksLastUpdateDate.isBefore(dates[2])) {
            updateNeeded = true;
            UpdateLocalService.setPksLastUpdate(this.momentToDateString(dates[2]));
          }

          return updateNeeded;
        });
    }
    return Observable.of(false);
  }

  clearData(): Observable<any> {
    return Observable.fromPromise(Promise.resolve(this.pkLocalService.clear()));
  }

  getData(page: number): Observable<any> {
    this.updateCurrentlyRunning = true;
    this.pages = 0;
    this.storedPages = 0;
    this.pkTotalPagesAdded = false;

    return Observable.forkJoin([
      this.getAllPksByPage(page),
      this.getWing1LimitThicknessData(),
      this.getEnums(),
      this.getObservationTypes()
    ]).flatMap(
      () => this.getLastUpdates()
    ).flatMap(
      (dates: string[]) => this.setLastUpdates(dates)
    )._finally(
      () => this.updateCurrentlyRunning = false
    )
  }

  getUpdateProgress(): number {
    return this.pkTotalPagesAdded ? Math.round(this.storedPages * 100 / this.pages) : 0;
  }

  resetProgress(): void {
    this.storedPages = 0;
    this.pages = 0;
    this.pkTotalPagesAdded = false;
  }

  private dateStringToMoment(dateString: string): Moment {
    return moment(dateString, 'YYYY-MM-DD HH:mm:ss');
  }

  private getAllPksByPage(page: number) {
    return this.pkService.getPks(page)
      .concatMap(data => {
        this.storePks(data.content)
          .then(() => {
            this.storedPages += 1;
          });
        if (data.first) {
          this.pages += data.totalPages;
          this.pkTotalPagesAdded = true;
          localStorage.setItem('totalPks', data.totalElements);
        }
        if (!data.last) {
          return this.getAllPksByPage(page + 1);
        } else {

          return this.pkLocalService.count().map((cnt) =>{
            // Force local count to indexdb count (duplicates on backend view)
            localStorage.setItem('totalPks', cnt.toString(10));
            return undefined;
          })
        }
      });
  }

  private getLastUpdates(): Observable<string[]> {
    return Observable.forkJoin(
      this.measureService.getMeasuresLastUpdate(),
      this.observationTypesService.getObservationTypesLastUpdate(),
      this.pkService.getPksLastUpdate()
    )
  }

  private getWing1LimitThicknessData() {
    this.pages++;
    return this.geometricDataService.getWing1LimitThicknessData()
      .map(alertThicknessData => {
        localStorage.setItem('alertThickness', JSON.stringify(alertThicknessData));
        this.storedPages++;
      })
  }

  private getEnums(): Observable<any> {
    this.pages++;
    return this.enumService.getEnums().map(
      enums => {
        localStorage.setItem('enums', JSON.stringify(enums));
        this.storedPages++;
      }
    );
  }

  private getObservationTypes(): Observable<any> {
    this.pages++;
    return this.observationTypesService.getObservationTypes().map(
      (observationTypes: ObservationType[]) => {
        localStorage.setItem('observationTypes', JSON.stringify(observationTypes));
        this.storedPages++;
      }
    );
  }

  private momentToDateString(momentDate: Moment): string {
    return momentDate.format('YYYY-MM-DD HH:mm:ss');
  }

  private setLastUpdates(dates: string[]): Observable<any> {
    UpdateLocalService.setMeasuresLastUpdate(dates[0]);
    UpdateLocalService.setObservationTypesLastUpdate(dates[1]);
    UpdateLocalService.setPksLastUpdate(dates[2]);
    return Observable.of(true);
  }

  private storePks(pks: PK[]) {
    pks.map(pk => {
      pk.number = pk.number.toString();
      return pk;
    });
    return this.pkLocalService.bulkAdd(pks);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/update.service.ts